import { Layout } from '@/components/layouts/layout';
import { Button } from '@/components/ui/button';
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { useSession } from '@/lib/auth-client';
import { getActiveTenant, isSubscriptionActive } from '@/lib/tenant';
import {
    createFileRoute,
    useNavigate,
    useRouterState,
} from '@tanstack/react-router';
import { CheckCircle2, Clock3 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';

export const Route = createFileRoute('/subscribe')({
    component: Subscribe,
});

const checkoutFlagKey = 'stockwright_checkout_pending';
const dashboardRedirectGuardKey = 'stockwright_dashboard_redirect_guard';

function Subscribe() {
    const { data: session } = useSession();
    const navigate = useNavigate();
    const location = useRouterState({ select: (state) => state.location });
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);
    const [isStatusChecking, setIsStatusChecking] = useState(false);
    const [shouldAutoCheck, setShouldAutoCheck] = useState(false);
    const [statusNotice, setStatusNotice] = useState<{
        tone: 'info' | 'success' | 'warning';
        message: string;
    } | null>(null);
    const [priceInfo, setPriceInfo] = useState<{
        unitAmount: number | null;
        currency: string | null;
        interval: string | null;
        productName: string | null;
        trialDays: number | null;
    } | null>(null);
    const [priceError, setPriceError] = useState<string | null>(null);
    const [accessCode, setAccessCode] = useState('');
    const isProcessingCheckout = shouldAutoCheck || isStatusChecking;

    const billingParam = useMemo(() => {
        const params = new URLSearchParams(location.search);
        return params.get('billing');
    }, [location.search]);

    const apiBase = useMemo(
        () =>
            (
                (import.meta.env.VITE_BILLING_API_BASE_URL as
                    | string
                    | undefined) ??
                (import.meta.env.VITE_API_BASE_URL as string | undefined)
            )?.replace(/\/$/, ''),
        [],
    );

    const formatPrice = useMemo(() => {
        return (
            amount: number | null,
            currency: string | null,
            interval: string | null,
        ) => {
            if (amount === null || !currency) {
                return null;
            }
            const formatted = new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: currency.toUpperCase(),
                maximumFractionDigits: 2,
            }).format(amount / 100);
            return interval ? `${formatted}/${interval}` : formatted;
        };
    }, []);

    useEffect(() => {
        let isMounted = true;

        const loadPrice = async () => {
            try {
                const response = await fetch(
                    `${apiBase ?? ''}/api/stripe/price`,
                );
                const payload = (await response.json()) as
                    | {
                          unitAmount?: number | null;
                          currency?: string | null;
                          interval?: string | null;
                          productName?: string | null;
                          trialDays?: number | null;
                      }
                    | undefined;

                if (!response.ok) {
                    throw new Error('Unable to load pricing.');
                }

                if (!isMounted) {
                    return;
                }

                setPriceInfo({
                    unitAmount: payload?.unitAmount ?? null,
                    currency: payload?.currency ?? null,
                    interval: payload?.interval ?? null,
                    productName: payload?.productName ?? null,
                    trialDays:
                        typeof payload?.trialDays === 'number'
                            ? payload.trialDays
                            : null,
                });
                setPriceError(null);
            } catch (err) {
                if (!isMounted) {
                    return;
                }
                setPriceError(
                    err instanceof Error
                        ? err.message
                        : 'Unable to load pricing.',
                );
            }
        };

        void loadPrice();

        return () => {
            isMounted = false;
        };
    }, [apiBase]);

    const readCheckoutFlag = useCallback(() => {
        try {
            const value = window.localStorage.getItem(checkoutFlagKey);
            if (!value) {
                return null;
            }
            const timestamp = Number(value);
            if (!Number.isFinite(timestamp)) {
                window.localStorage.removeItem(checkoutFlagKey);
                return null;
            }
            if (Date.now() - timestamp > 1000 * 60 * 60) {
                window.localStorage.removeItem(checkoutFlagKey);
                return null;
            }
            return timestamp;
        } catch {
            return null;
        }
    }, []);

    const setCheckoutFlag = useCallback(() => {
        try {
            window.localStorage.setItem(checkoutFlagKey, Date.now().toString());
        } catch {
            // Ignore localStorage issues.
        }
    }, []);

    const clearCheckoutFlag = useCallback(() => {
        try {
            window.localStorage.removeItem(checkoutFlagKey);
        } catch {
            // Ignore localStorage issues.
        }
    }, []);

    const readDashboardRedirectGuard = useCallback(() => {
        try {
            const value = window.sessionStorage.getItem(
                dashboardRedirectGuardKey,
            );
            if (!value) {
                return null;
            }
            const timestamp = Number(value);
            if (!Number.isFinite(timestamp)) {
                window.sessionStorage.removeItem(dashboardRedirectGuardKey);
                return null;
            }
            if (Date.now() - timestamp > 10_000) {
                window.sessionStorage.removeItem(dashboardRedirectGuardKey);
                return null;
            }
            return timestamp;
        } catch {
            return null;
        }
    }, []);

    const setDashboardRedirectGuard = useCallback(() => {
        try {
            window.sessionStorage.setItem(
                dashboardRedirectGuardKey,
                Date.now().toString(),
            );
        } catch {
            // Ignore sessionStorage issues.
        }
    }, []);

    const shouldSuppressDashboardRedirect = useCallback(
        () => readDashboardRedirectGuard() !== null,
        [readDashboardRedirectGuard],
    );

    useEffect(() => {
        let isMounted = true;

        void getActiveTenant({ force: true })
            .then((tenant) => {
                if (!isMounted) {
                    return;
                }
                if (isSubscriptionActive(tenant.subscriptionStatus)) {
                    if (!shouldSuppressDashboardRedirect()) {
                        setDashboardRedirectGuard();
                        navigate({ to: '/dashboard', replace: true });
                        return;
                    }
                    setStatusNotice({
                        tone: 'warning',
                        message:
                            "You're already subscribed. If you aren't redirected automatically, open your dashboard from the menu.",
                    });
                }
                setIsLoading(false);
            })
            .catch((err) => {
                if (!isMounted) {
                    return;
                }
                setError(
                    err instanceof Error
                        ? err.message
                        : 'Unable to load subscription status.',
                );
                setIsLoading(false);
            });

        return () => {
            isMounted = false;
        };
    }, [navigate, setDashboardRedirectGuard, shouldSuppressDashboardRedirect]);

    const checkSubscriptionStatus = useCallback(async () => {
        setIsStatusChecking(true);
        setError(null);
        setStatusNotice({
            tone: 'info',
            message: 'Checking Stripe for your subscription...',
        });

        try {
            const tenant = await getActiveTenant({ force: true });
            if (isSubscriptionActive(tenant.subscriptionStatus)) {
                if (shouldSuppressDashboardRedirect()) {
                    clearCheckoutFlag();
                    setStatusNotice({
                        tone: 'warning',
                        message:
                            "You're already subscribed. If you aren't redirected automatically, open your dashboard from the menu.",
                    });
                    return true;
                }
                setStatusNotice({
                    tone: 'success',
                    message:
                        'Subscription active. Taking you to your dashboard…',
                });
                clearCheckoutFlag();
                setDashboardRedirectGuard();
                navigate({ to: '/dashboard', replace: true });
                return true;
            }

            setStatusNotice({
                tone: 'warning',
                message:
                    'We have not received an active subscription yet. If you just paid, give it a moment while we keep checking.',
            });
            return false;
        } catch (err) {
            setError(
                err instanceof Error
                    ? err.message
                    : 'Unable to check subscription status.',
            );
            setStatusNotice(null);
            return false;
        } finally {
            setIsStatusChecking(false);
        }
    }, [
        clearCheckoutFlag,
        navigate,
        setDashboardRedirectGuard,
        shouldSuppressDashboardRedirect,
    ]);

    const trialDays = priceInfo?.trialDays ?? null;
    const hasTrial = typeof trialDays === 'number' && trialDays > 0;
    const billingIntervalLabel = priceInfo?.interval
        ? `Billed ${priceInfo.interval}.`
        : 'Billed monthly.';
    const billingSummary = hasTrial
        ? `Free for ${trialDays} days. ${billingIntervalLabel} Cancel any time. Card required.`
        : `${billingIntervalLabel} Cancel any time.`;
    const termsSummary = hasTrial
        ? `By continuing you agree to the Stripe checkout terms. Your ${trialDays}-day trial starts today. We'll charge after ${trialDays} days unless you cancel.`
        : 'By continuing you agree to the Stripe checkout terms. Your plan starts immediately after confirmation.';

    useEffect(() => {
        const hasPendingCheckout =
            typeof window !== 'undefined' && readCheckoutFlag() !== null;
        if (!billingParam && !hasPendingCheckout) {
            return;
        }
        setShouldAutoCheck(true);
        void checkSubscriptionStatus();
    }, [billingParam, checkSubscriptionStatus, readCheckoutFlag]);

    useEffect(() => {
        if (!shouldAutoCheck) {
            return;
        }

        let isCancelled = false;
        let attempts = 0;
        const maxAttempts = 4;

        const interval = setInterval(async () => {
            if (isCancelled) {
                return;
            }
            attempts += 1;
            const isActive = await checkSubscriptionStatus();
            if (isActive || attempts >= maxAttempts) {
                clearInterval(interval);
                setShouldAutoCheck(false);
                if (!isActive && attempts >= maxAttempts) {
                    clearCheckoutFlag();
                    setStatusNotice({
                        tone: 'warning',
                        message:
                            'No active subscription found. If you canceled checkout, you can start again below.',
                    });
                }
            }
        }, 4000);

        return () => {
            isCancelled = true;
            clearInterval(interval);
        };
    }, [shouldAutoCheck, checkSubscriptionStatus, clearCheckoutFlag]);

    const startCheckout = async () => {
        if (!session?.access_token) {
            setError('You must be signed in to subscribe.');
            return;
        }

        setIsCheckoutLoading(true);
        setError(null);
        setStatusNotice({
            tone: 'info',
            message: 'Redirecting to Stripe for secure checkout...',
        });

        try {
            const tenant = await getActiveTenant();
            const trimmedAccessCode = accessCode.trim();
            const response = await fetch(
                `${apiBase ?? ''}/api/stripe/checkout`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${session.access_token}`,
                    },
                    body: JSON.stringify({
                        tenantId: tenant.id,
                        ...(trimmedAccessCode
                            ? { accessCode: trimmedAccessCode }
                            : {}),
                    }),
                },
            );

            const payload = (await response.json()) as
                | { url?: string; error?: string }
                | undefined;

            if (!response.ok) {
                throw new Error(payload?.error ?? 'Unable to start billing.');
            }

            if (payload?.url) {
                setCheckoutFlag();
                window.location.assign(payload.url);
                return;
            }

            throw new Error('Stripe did not return a checkout link.');
        } catch (err) {
            setError(
                err instanceof Error ? err.message : 'Unable to start billing.',
            );
        } finally {
            setIsCheckoutLoading(false);
        }
    };

    if (isProcessingCheckout) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-background px-4 py-10">
                <Card className="w-full max-w-md border-border/70 bg-card shadow-sm">
                    <CardHeader>
                        <CardTitle>Processing subscription</CardTitle>
                        <CardDescription>
                            We&apos;re confirming your subscription with Stripe.
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="rounded-md border border-border bg-muted/40 px-3 py-3 text-sm text-muted-foreground">
                            <div className="flex items-center gap-3">
                                <Spinner className="text-primary" />
                                <div>
                                    <div className="text-sm font-medium text-foreground">
                                        This usually takes a few seconds.
                                    </div>
                                    <div className="text-xs text-muted-foreground">
                                        We&apos;ll redirect you automatically
                                        once Stripe confirms your subscription.
                                    </div>
                                </div>
                            </div>
                        </div>
                        {error && (
                            <div className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                                {error}
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <Layout>
            <div className="mx-auto w-full max-w-5xl px-4 py-10">
                <div className="mb-8 rounded-lg border border-border/60 bg-card/40 px-4 py-3">
                    <div className="flex flex-wrap items-center justify-around gap-3 text-xs uppercase tracking-[0.2em] text-muted-foreground">
                        <span className="rounded-full bg-emerald-500/15 px-3 py-1 text-emerald-500">
                            Step 1 · Account created
                        </span>
                        <span className="rounded-full bg-primary/15 px-3 py-1 text-primary">
                            Step 2 · Subscribe
                        </span>
                        <span className="rounded-full bg-muted/60 px-3 py-1 text-muted-foreground">
                            Step 3 · Dashboard
                        </span>
                    </div>
                </div>
                <div className="grid gap-8 lg:grid-cols-[1.1fr,0.9fr] lg:items-start">
                    <div className="space-y-6">
                        <div className="space-y-2">
                            <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
                                You&apos;re almost there
                            </p>
                            <h1 className="text-3xl font-semibold leading-tight sm:text-4xl">
                                Unlock your Stockwright workspace
                            </h1>
                            <p className="text-base text-muted-foreground">
                                {hasTrial
                                    ? "Start your free trial to track inventory, monitor margins, and see what's driving profits across every channel."
                                    : "Start your subscription to track inventory, monitor margins, and see what's driving profits across every channel."}
                            </p>
                        </div>

                        <div className="space-y-3 rounded-lg border border-border/60 bg-card/40 p-4 text-sm">
                            {[
                                'Live profit and sell-through insights',
                                'Multi-channel inventory tracking',
                                'Centralized subscriptions & billing',
                                'Priority updates as new analytics ship',
                            ].map((item) => (
                                <div
                                    key={item}
                                    className="flex items-center gap-3"
                                >
                                    <CheckCircle2 className="size-4 text-emerald-500" />
                                    <span>{item}</span>
                                </div>
                            ))}
                        </div>

                        <div className="text-sm text-muted-foreground">
                            You can manage billing any time from your account
                            page. We&apos;ll take you back to your dashboard as
                            soon as Stripe confirms the subscription.
                        </div>
                    </div>

                    <Card className="border-border/70 bg-card shadow-sm">
                        <CardHeader>
                            <CardTitle>
                                {hasTrial
                                    ? 'Start your free trial'
                                    : 'Start your subscription'}
                            </CardTitle>
                            <CardDescription>
                                Secure checkout powered by Stripe.
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="rounded-lg border border-border/60 bg-muted/40 px-4 py-3 text-sm">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
                                            Plan
                                        </div>
                                        <div className="text-base font-semibold">
                                            {priceInfo?.productName ??
                                                'Stockwright Pro'}
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
                                            Price
                                        </div>
                                        <div className="text-lg font-semibold">
                                            {priceError
                                                ? 'Unavailable'
                                                : (formatPrice(
                                                      priceInfo?.unitAmount ??
                                                          null,
                                                      priceInfo?.currency ??
                                                          null,
                                                      priceInfo?.interval ??
                                                          'month',
                                                  ) ?? 'Loading...')}
                                        </div>
                                    </div>
                                </div>
                                <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
                                    <Clock3 className="size-3" />
                                    {billingSummary}
                                </div>
                                {priceError && (
                                    <div className="mt-3 text-xs text-muted-foreground">
                                        {priceError}
                                    </div>
                                )}
                            </div>
                            <div className="space-y-2">
                                <label
                                    htmlFor="subscribe-access-code"
                                    className="text-xs uppercase tracking-[0.2em] text-muted-foreground"
                                >
                                    Referral code (optional)
                                </label>
                                <Input
                                    id="subscribe-access-code"
                                    value={accessCode}
                                    onChange={(event) =>
                                        setAccessCode(event.target.value)
                                    }
                                    placeholder="Enter your access code"
                                    autoComplete="off"
                                    spellCheck={false}
                                />
                                <p className="text-xs text-muted-foreground">
                                    Have a free access code? Enter it here and
                                    we&apos;ll apply it automatically at
                                    checkout.
                                </p>
                            </div>
                            {isLoading && (
                                <div className="rounded-md border border-border bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
                                    Checking your workspace...
                                </div>
                            )}
                            {isProcessingCheckout && (
                                <div className="rounded-md border border-border bg-muted/40 px-3 py-3 text-sm text-muted-foreground">
                                    <div className="flex items-center gap-3">
                                        <Spinner className="text-primary" />
                                        <div>
                                            <div className="text-sm font-medium text-foreground">
                                                Processing your subscription
                                            </div>
                                            <div className="text-xs text-muted-foreground">
                                                This can take a few seconds.
                                                We&apos;ll redirect you
                                                automatically once Stripe
                                                confirms.
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                            {statusNotice && !isProcessingCheckout && (
                                <div
                                    className={
                                        statusNotice.tone === 'success'
                                            ? 'rounded-md border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-500'
                                            : statusNotice.tone === 'warning'
                                              ? 'rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-600'
                                              : 'rounded-md border border-border bg-muted/40 px-3 py-2 text-sm text-muted-foreground'
                                    }
                                >
                                    {statusNotice.message}
                                </div>
                            )}
                            {error && (
                                <div className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                                    {error}
                                </div>
                            )}
                            {!isProcessingCheckout && (
                                <>
                                    <Button
                                        onClick={startCheckout}
                                        disabled={
                                            isCheckoutLoading || isLoading
                                        }
                                        className="w-full"
                                    >
                                        {isCheckoutLoading
                                            ? 'Redirecting to Stripe...'
                                            : hasTrial
                                              ? 'Start free trial'
                                              : 'Start subscription'}
                                    </Button>
                                    <p className="text-xs text-muted-foreground">
                                        You&apos;ll complete checkout on Stripe.
                                        Stripe will show the merchant as Lobby
                                        Studio, the parent company of
                                        Stockwright.
                                    </p>
                                    <p className="text-xs text-muted-foreground">
                                        {termsSummary}
                                    </p>
                                </>
                            )}
                        </CardContent>
                    </Card>
                </div>
            </div>
        </Layout>
    );
}
